import { useTranslation } from "react-i18next";
import { Inbox } from "lucide-react";
import clsx from "clsx";
import { UNSORTED_FOLDER_OPTION } from "../constants";
import { FolderListItem } from "./FolderListItem";
import type { FolderListPaneProps } from "./types";

export const FolderListPane = ({
  folders,
  planIdsByFolderKey,
  selectedKey,
  alreadyFiledPlanCount,
  overwrite,
  isDisabled,
  onSelect,
  onOverwriteChange,
}: FolderListPaneProps) => {
  const { t } = useTranslation();

  const isUnsortedSelected = selectedKey === UNSORTED_FOLDER_OPTION;

  return (
    <nav
      aria-label={t("smartSurveyor.folders.suggestion.foldersHeading")}
      className="flex w-72 flex-shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200"
    >
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-3 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        <span>{t("smartSurveyor.folders.suggestion.foldersHeading")}</span>
        <span>{folders.length}</span>
      </div>
      <ul className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-1.5">
        {folders.map((folder) => (
          <FolderListItem
            key={folder.key}
            folder={folder}
            planCount={planIdsByFolderKey.get(folder.key)?.length ?? 0}
            isSelected={folder.key === selectedKey}
            onSelect={onSelect}
          />
        ))}
      </ul>
      <div className="flex-shrink-0 border-t border-gray-200 p-1.5">
        <button
          type="button"
          aria-current={isUnsortedSelected}
          onClick={() => onSelect(UNSORTED_FOLDER_OPTION)}
          className={clsx(
            "flex w-full items-center gap-2 rounded-md border border-dashed px-2.5 py-1.5 text-left text-sm",
            isUnsortedSelected
              ? "border-blue-300 bg-blue-50 text-blue-700"
              : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100",
          )}
        >
          <Inbox size={16} className="flex-shrink-0" />
          <span className="flex-1 font-semibold">
            {t("smartSurveyor.folders.suggestion.unsorted")}
          </span>
          <span
            className={clsx(
              "flex-shrink-0 text-xs font-medium tabular-nums",
              isUnsortedSelected ? "text-blue-700" : "text-gray-500",
            )}
          >
            {planIdsByFolderKey.get(null)?.length ?? 0}
          </span>
        </button>
      </div>
      {alreadyFiledPlanCount > 0 && (
        <label className="flex flex-shrink-0 items-start gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(event) => onOverwriteChange(event.target.checked)}
            disabled={isDisabled}
            className="mt-0.5 h-4 w-4 flex-shrink-0"
          />
          <span>
            {t("smartSurveyor.folders.suggestion.overwrite")}{" "}
            <span className="text-gray-500">
              (
              {t("smartSurveyor.folders.suggestion.planCount", {
                count: alreadyFiledPlanCount,
              })}
              )
            </span>
          </span>
        </label>
      )}
    </nav>
  );
};
